'use client'

import React from 'react'

interface Props {
    merchantName: string
    merchantDetail?: string | null
    lastVerifiedDate: string
    siteName?: string
}

const StoreGuideSections = ({
    merchantName,
    merchantDetail,
    lastVerifiedDate,
    siteName = ''
}: Props) => {
    const howToSteps = [
        `Click "Get Code" on the offer you want and copy the code.`,
        `Open ${merchantName}'s website in the tab that opens.`,
        `Add your items to the cart.`,
        `Paste the code in the promo or coupon box at checkout.`,
        `Check your total. Make sure the discount shows before you pay.`
    ]

    const defaultSavingTips = [
        `Check for a first-order offer. Some stores reward new customers or email sign-ups.`,
        `Shop big sales. Black Friday and Cyber Monday are good times to compare prices.`,
        `Compare bundles. A kit can cost less than buying each sensor or item separately.`,
        `Watch the shipping minimum. Adding one item can sometimes unlock free shipping.`,
        `Read the terms. Codes often exclude some products or other promotions.`
    ]

    const ezTaxReturnSavingTips = [
        `Check free filing first. Simple federal returns with wage income under $16,000 and no dependents might cost you nothing at ezTaxReturn.`,
        `Start at IRS.gov for Free File. If you may qualify, begin at IRS.gov/freefile, since going to the site directly won’t give free filing.`,
        `Pick a bundle offer. If you need federal and state returns, a bundle discount saves most, since the combined price has no built-in cut.`,
        `Apply your code before paying. The coupon box appears just before the payment screen, and discounts can’t be added after checkout ends.`,
        `Skip extras you don’t need. Paying fees from your refund, Audit Defense and printed copies add cost, so review the fee breakdown first.`
    ]

    const biOptimizersSavingTips = [
        `Check for a first-order offer. Sign up for the email list — new subscribers sometimes get an exclusive BiOptimizers coupon code at registration.`,
        `Buy 3 or 5 bottles at once. Multi-bottle bulk pricing saves up to 32–68% per bottle automatically — no promo code needed for this discount.`,
        `Use subscribe-and-save for ~12% off. Automatic deliveries apply the subscription discount to every order without needing a separate coupon code.`,
        `Consolidate orders above $100. Hitting the free shipping threshold on US orders removes the shipping fee — a 3-bottle order usually qualifies.`,
        `Compare coupon vs. bulk pricing. A percentage-off coupon code may beat the built-in tier discount — or vice versa. Check both before checkout.`
    ]

    const abbyySavingTips = [
        `Start with the 7-day free trial — no credit card needed. Test OCR accuracy on your own documents before committing to a plan.`,
        `Pick annual or 3-year billing over monthly. A 3-year Standard plan saves up to 54% compared to paying month by month.`,
        `Choose Standard unless you need batch automation. Corporate costs $66/yr more and is only worth it for Hot Folder features.`,
        `Check this page for an ABBYY coupon code before checkout. A percentage-off deal may beat the built-in multi-year discount.`,
        `Students and educators should verify academic pricing on abbyy.com. The education discount may give a lower total than a coupon.`
    ]

    const airAdvisorSavingTips = [
        `File complete paperwork on the first attempt — missing documents delay claims and can push settlements into the costly legal-action tier.`,
        `Use the free eligibility checker before committing so you know the exact compensation amount and can calculate your net payout after fees.`,
        `Claim for every passenger on the booking individually — a family of four can recover up to €2,400 on a single disrupted flight.`,
        `Never accept airline vouchers without first comparing their value to what a claims service would recover minus fees.`,
        `File within the EU261 deadline (up to 3 years in most EU countries) — waiting too long is the most common reason otherwise valid claims are rejected.`
    ]

    const merchantNameNormalized = merchantName.toLowerCase()
    const savingTips = merchantNameNormalized.includes('eztaxreturn')
        ? ezTaxReturnSavingTips
        : merchantNameNormalized.includes('bioptimizers')
            ? biOptimizersSavingTips
            : (merchantNameNormalized.includes('abbyy') || merchantNameNormalized.includes('abby'))
                ? abbyySavingTips
                : merchantNameNormalized.includes('airadvisor')
                    ? airAdvisorSavingTips
                    : defaultSavingTips

    // Formatted fallback about text if merchantDetail is short/empty
    const cleanDetail = merchantDetail ? merchantDetail.replace(/<[^>]+>/g, '').trim() : ''

    return (
        <div className="space-y-10 pt-4">
            {/* ── SECTION: HOW TO USE ── */}
            <section id="how-to-use" className="scroll-mt-24">
                <h2 className="text-xl sm:text-2xl font-bold text-[#071822] mb-4">
                    How to Use an {merchantName} Coupon Code
                </h2>
                <div className="bg-white rounded-2xl border border-slate-200/90 p-6 sm:p-7 shadow-xs">
                    <ol className="space-y-4">
                        {howToSteps.map((step, idx) => (
                            <li key={idx} className="flex items-start gap-4">
                                <span className="w-6 h-6 rounded-full bg-[#00875a] text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                                    {idx + 1}
                                </span>
                                <span className="text-xs sm:text-sm text-slate-800 leading-relaxed font-normal">
                                    {step}
                                </span>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            {/* ── SECTION: ABOUT STORE ── */}
            <section id="about" className="scroll-mt-24">
                <h2 className="text-xl sm:text-2xl font-bold text-[#071822] mb-4">
                    About {merchantName}
                </h2>
                <div className="bg-white rounded-2xl border border-slate-200/90 p-6 sm:p-7 shadow-xs space-y-3.5">
                    {cleanDetail ? (
                        <div
                            className="text-xs sm:text-sm text-slate-800 leading-relaxed prose prose-sm max-w-none font-normal prose-a:text-[#00a87a] prose-a:font-semibold prose-a:hover:text-[#00875a]"
                            dangerouslySetInnerHTML={{ __html: merchantDetail || '' }}
                        />
                    ) : (
                        <>
                            <p className="text-xs sm:text-sm text-slate-800 leading-relaxed font-normal">
                                {merchantName} is a recognized brand providing quality products and reliable customer support.
                                Shoppers visit {merchantName} for competitive prices, authentic merchandise, and frequent seasonal sales.
                            </p>
                            <p className="text-xs sm:text-sm text-slate-800 leading-relaxed font-normal">
                                We regularly update this page with the newest verified discount codes, seasonal deals, and checkout promotions
                                directly from {merchantName}&apos;s official store.
                            </p>
                        </>
                    )}
                </div>
            </section>

            {/* ── SECTION: TIPS TO SAVE MORE ── */}
            <section id="saving-tips" className="scroll-mt-24">
                <h2 className="text-xl sm:text-2xl font-bold text-[#071822] mb-4">
                    Tips to Save More at {merchantName}
                </h2>
                <div className="bg-white rounded-2xl border border-slate-200/90 p-6 sm:p-7 shadow-xs">
                    <ol className="space-y-4">
                        {savingTips.map((tip, idx) => (
                            <li key={idx} className="flex items-start gap-4">
                                <span className="w-6 h-6 rounded-full bg-[#00875a] text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                                    {idx + 1}
                                </span>
                                <span className="text-xs sm:text-sm text-slate-800 leading-relaxed font-normal">
                                    {tip}
                                </span>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>
            {/* ── EDITORIAL REVIEW BOX ── */}
            <div className="bg-white rounded-2xl border border-slate-200/90 p-4 sm:p-5 shadow-xs flex items-center gap-4">
                <div className="w-11 h-11 rounded-full bg-[#00875a] text-white font-extrabold text-sm flex items-center justify-center shrink-0 tracking-wider">
                    ED
                </div>
                <div>
                    <p className="text-xs sm:text-sm font-bold text-[#071822]">
                        Reviewed by Editorial Team, {siteName} Editorial Team
                    </p>
                    <p className="text-xs text-slate-800 mt-0.5">
                        Last reviewed {lastVerifiedDate} · <span className="text-[#00875a] hover:underline cursor-pointer">How we verify coupons</span>
                    </p>
                </div>
            </div>
        </div>
    )
}

export default StoreGuideSections
